"use client"

import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Lock } from "lucide-react"
import Link from "next/link"
import Image from "next/image"
import { usePathname } from "next/navigation"
import { usePlatform, triggerHaptic } from "@/hooks/use-platform"

interface AuthWallProps {
  title?: string
  description?: string
  variant?: "inline" | "card" | "full"
  className?: string
}

/**
 * AuthWall
 * 
 * A reusable component to prompt users to sign in when they reach
 * a protected part of the application.
 */
export function AuthWall({ 
  title = "Members Only Feature", 
  description = "Join the community to unlock this feature and connect with other surfers.",
  variant = "card",
  className = ""
}: AuthWallProps) {
  const platform = usePlatform()
  const pathname = usePathname()
  const redirectTo = encodeURIComponent(pathname)

  if (variant === "inline") {
    return (
      <div className={`flex flex-col items-center justify-center p-8 text-center space-y-6 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm rounded-[2.5rem] border border-slate-100 dark:border-slate-800 shadow-xl shadow-slate-200/50 dark:shadow-none ${className}`}>
        <div className="w-16 h-16 bg-blue-50 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
          <Lock className="w-8 h-8 text-blue-600 dark:text-blue-400" />
        </div>
        <div className="space-y-2">
          <p className="text-lg font-black uppercase tracking-tight text-slate-900 dark:text-slate-100">{title}</p>
          <p className="text-sm font-medium text-slate-500 dark:text-slate-400 max-w-[240px] leading-relaxed">{description}</p>
        </div>
        <div className="flex flex-col gap-3 w-full max-w-[240px]">
          <Button asChild className="h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl font-bold shadow-lg shadow-blue-600/20 active:scale-95 transition-all" onClick={() => triggerHaptic('impact')}>
            <Link href="/auth/register">Join Community</Link>
          </Button>
          <Button asChild variant="ghost" className="h-10 text-slate-500 dark:text-slate-400 font-bold hover:bg-slate-50 dark:hover:bg-slate-800 rounded-xl" onClick={() => triggerHaptic('impact')}>
            <Link href={`/auth/login?redirectTo=${redirectTo}`}>Already a member? Sign In</Link>
          </Button>
        </div>
      </div>
    )
  }

  if (variant === "full") {
    return (
      <div className={`flex flex-col items-center justify-center min-h-[400px] p-8 text-center space-y-6 ${className}`}>
        <div className="w-20 h-20 mb-2">
          <Image src="/spotstosurf-transparant.png" alt="Logo" width={80} height={80} className="object-contain" />
        </div>
        <div className="space-y-2 max-w-sm">
          <h2 className="text-3xl font-black text-slate-900 dark:text-slate-50 tracking-tighter uppercase italic leading-none">SPOTS2SURF</h2>
          <p className="text-slate-500 dark:text-slate-400 font-bold uppercase tracking-widest text-[10px] italic">Join the surf community</p>
        </div>
        <div className="flex flex-col w-full max-w-xs gap-3">
          <Button asChild size="lg" className="bg-blue-500 hover:bg-blue-600 text-white rounded-2xl h-16 font-black uppercase tracking-widest shadow-xl shadow-blue-500/20 active:scale-95 transition-all" onClick={() => triggerHaptic('success')}>
            <Link href="/auth/register">Get Lifetime Access</Link>
          </Button>
          <Button asChild variant="ghost" size="lg" className="rounded-2xl h-14 font-bold text-slate-500 hover:text-slate-900" onClick={() => triggerHaptic('impact')}>
            <Link href={`/auth/login?redirectTo=${redirectTo}`}>Already a member? Sign In</Link>
          </Button>
        </div>
        <div className="space-y-2">
          <p className="text-[10px] text-blue-500 font-black uppercase tracking-[0.2em] italic">🎁 First 250 get free lifetime access</p>
          <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">One-time payment • Lifetime membership</p>
        </div>
      </div>
    )
  }

  const showStripe = !platform.isNative && !platform.isIOS
  const showApple = platform.isIOS || platform.isNative

  return (
    <Card className={`bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm border border-slate-100 dark:border-slate-800 rounded-[3rem] overflow-hidden shadow-xl shadow-slate-200/50 dark:shadow-none ${className}`}>
      <CardContent className="p-10 flex flex-col items-center text-center space-y-6">
        <div className="w-20 h-20 bg-blue-50 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
          <Lock className="w-10 h-10 text-blue-600 dark:text-blue-400" />
        </div>
        <div className="space-y-2">
          <h3 className="font-black text-2xl text-slate-900 dark:text-slate-100 uppercase tracking-tight">{title}</h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 max-w-xs font-medium italic leading-relaxed">{description}</p>
        </div>
        <div className="flex flex-col w-full gap-3 pt-2">
          <Button asChild className="bg-blue-600 hover:bg-blue-500 text-white rounded-2xl h-14 font-black uppercase tracking-widest shadow-lg shadow-blue-500/25 active:scale-95 transition-all border-none" onClick={() => triggerHaptic('success')}>
            <Link href="/auth/register">Join Community</Link>
          </Button>
          <Button asChild variant="ghost" className="h-12 rounded-xl font-bold text-slate-500" onClick={() => triggerHaptic('impact')}>
            <Link href={`/auth/login?redirectTo=${redirectTo}`}>Sign in to your account</Link>
          </Button>
        </div>
        <div className="pt-2 space-y-2">
          <p className="text-[10px] text-blue-500 font-black uppercase tracking-[0.2em] italic animate-pulse">
            🎁 First 250 members get free lifetime access
          </p>
          {showStripe && (
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest opacity-50 italic">Secure checkout with Stripe</p>
          )}
          {showApple && (
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest opacity-50 italic">Secure payment via Apple</p>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
